import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createChart, createSeriesMarkers, AreaSeries, ColorType, CrosshairMode } from 'lightweight-charts';
import { api } from '../api/client';
import { getTradeMarkerColors } from '../settings';
import { barOnOrAfter } from '../chartMarkers';

const COLORS = {
  border: '#2a2d3a',
  muted: '#64748b',
  text: '#f1f5f9',
  accent: '#3b82f6',
  gain: '#22c55e',
  loss: '#ef4444',
};
const RANGES = [
  { id: '1W', days: 7 },
  { id: '1M', days: 30 },
  { id: '3M', days: 91 },
  { id: '1Y', days: 365 },
  { id: 'All', days: null },
];
// Money crossing the account boundary. Shown as markers and left out of return.
const FLOW_TYPES = {
  deposit: { label: 'Deposit', sign: 1, color: COLORS.gain },
  opening_balance: { label: 'Opening balance', sign: 1, color: COLORS.accent },
  withdrawal: { label: 'Withdrawal', sign: -1, color: COLORS.loss },
};

const fmt = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(Number(n)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (n) => (n >= 0 ? '+' : '') + fmt(n);
const fmtPct = (p) => (p >= 0 ? '+' : '') + p.toFixed(2) + '%';
const fmtQty = (q) => Number(Number(q).toFixed(4)).toLocaleString();
const esc = (s) => String(s).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
const addDays = (isoDate, days) => {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

// Account balance over time (holdings + cash) for all accounts or one, with trade
// and cash-flow markers. Return is measured from the first date in view, net of
// deposits and withdrawals, and is rebased whenever the visible window changes.
// `refreshKey` changes whenever the parent reloads its data.
export default function BalanceChart({ accountId, accounts, onNavigate, refreshKey }) {
  const containerRef = useRef(null);
  const legendRef = useRef(null);
  const chartRef = useRef(null);
  const baseRef = useRef({ adjusted: 0, balance: 0 }); // window start, for % axis labels
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  const [history, setHistory] = useState(null);
  const [trades, setTrades] = useState([]);
  const [cash, setCash] = useState([]);
  const [range, setRange] = useState('All');
  const [mode, setMode] = useState('$');
  const [includeAccrued, setIncludeAccrued] = useState(false);
  const [view, setView] = useState(null); // { from, to } indexes of the bars in view
  const [markerColors] = useState(getTradeMarkerColors);

  useEffect(() => {
    let cancelled = false;
    const params = accountId ? { account_id: accountId } : {};
    Promise.all([
      api.getBalanceHistory(accountId || undefined),
      api.getTransactions(params),
      api.getCash(params),
    ]).then(([h, t, c]) => {
      if (cancelled) return;
      setHistory(h);
      setTrades(t || []);
      setCash(c || []);
    }).catch(() => { if (!cancelled) setHistory({ points: [], unpriced_symbols: [] }); });
    return () => { cancelled = true; };
  }, [accountId, refreshKey]);

  const hasAccrued = useMemo(() => !!history && history.points.some(p => p.accrued_estimate > 0), [history]);
  const withAccrued = includeAccrued && hasAccrued;

  // One row per trading day. `flows` is cumulative external money in/out, so
  // `adjusted` (balance - flows) moves only with investment return: over any
  // window, return = change in adjusted, and % return = that / starting balance.
  const rows = useMemo(() => {
    if (!history) return [];
    let flows = 0;
    return history.points.map((p, i) => {
      if (i > 0) flows += p.net_flow;
      const balance = p.balance + (withAccrued ? p.accrued_estimate : 0);
      return { ...p, time: p.date, balance, recorded: p.balance, flows, adjusted: balance - flows };
    });
  }, [history, withAccrued]);

  // Events grouped onto the bar they are drawn at, one marker per bar and kind.
  const groups = useMemo(() => {
    if (!rows.length) return [];
    const first = rows[0].time;
    const byId = new Map();
    const add = (date, kind, item) => {
      if (date < first) return; // already part of the opening balance
      const bar = barOnOrAfter(rows, date);
      if (!bar) return;
      const id = `${bar.time}:${kind}`;
      if (!byId.has(id)) byId.set(id, { id, time: bar.time, kind, items: [] });
      byId.get(id).items.push(item);
    };
    trades.forEach(t => {
      if (t.transaction_type === 'buy' || t.transaction_type === 'sell') add(t.date, t.transaction_type, t);
    });
    cash.forEach(c => { if (FLOW_TYPES[c.type]) add(c.date, c.type, c); });
    return [...byId.values()].sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));
  }, [rows, trades, cash]);

  // Build the chart. Rebuilt when the data, mode or markers change; panning and
  // range buttons only move the visible window.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || rows.length < 2) return undefined;

    const chart = createChart(node, {
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: COLORS.muted, fontSize: 11 },
      grid: { vertLines: { color: COLORS.border }, horzLines: { color: COLORS.border } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: COLORS.border, scaleMargins: { top: 0.15, bottom: 0.15 } },
      timeScale: { borderColor: COLORS.border },
      autoSize: true,
    });
    chartRef.current = chart;

    const percent = mode === '%';
    const series = chart.addSeries(AreaSeries, {
      lineColor: COLORS.accent,
      topColor: 'rgba(59, 130, 246, 0.28)',
      bottomColor: 'rgba(59, 130, 246, 0.02)',
      lineWidth: 2,
      priceLineVisible: false,
      // In % mode the line is the flow-adjusted balance; the axis relabels it as
      // cumulative return from the left edge of the view, so panning never reloads data.
      priceFormat: percent
        ? {
            type: 'custom',
            minMove: 0.01,
            formatter: v => {
              const base = baseRef.current;
              return base.balance > 0 ? fmtPct(((v - base.adjusted) / base.balance) * 100) : '—';
            },
          }
        : { type: 'custom', minMove: 0.01, formatter: v => fmt(v) },
    });
    series.setData(rows.map(r => ({ time: r.time, value: percent ? r.adjusted : r.balance })));

    createSeriesMarkers(series, groups.map(g => {
      const flow = FLOW_TYPES[g.kind];
      const outgoing = g.kind === 'sell' || (flow && flow.sign < 0);
      return {
        id: g.id,
        time: g.time,
        position: outgoing ? 'aboveBar' : 'belowBar',
        shape: flow ? 'circle' : (g.kind === 'buy' ? 'arrowUp' : 'arrowDown'),
        color: flow ? flow.color : markerColors[g.kind],
      };
    }));

    const rowByTime = new Map(rows.map(r => [r.time, r]));
    const groupsById = new Map(groups.map(g => [g.id, g]));
    const groupsByTime = new Map();
    groups.forEach(g => groupsByTime.set(g.time, [...(groupsByTime.get(g.time) || []), g]));
    const accountNames = new Map(accounts.map(a => [a.id, a.name]));
    const showAccount = !accountId && accounts.length > 1;
    const accountSuffix = (id) => (showAccount ? ` <span style="color:${COLORS.muted}">${esc(accountNames.get(id) || id)}</span>` : '');

    // Floating label: the hovered day's balance breakdown and what happened that day.
    const legendEl = legendRef.current;
    const renderLegend = (row) => {
      if (!legendEl) return;
      legendEl.style.display = row ? 'block' : 'none';
      if (!row) { legendEl.innerHTML = ''; return; }
      const label = (k, v) => `<span style="color:${COLORS.muted}">${k}</span> ${v}`;
      const lines = [
        `<span style="color:${COLORS.muted}">${row.time}</span>`,
        label('Balance', fmt(row.balance)),
        label('Holdings', fmt(row.holdings_value)),
        label('Cash', fmt(row.cash)),
      ];
      if (hasAccrued) {
        lines.push(label('Accrued money market income (estimate)', fmt(row.accrued_estimate))
          + (withAccrued ? ` <span style="color:${COLORS.muted}">included in balance</span>` : ''));
      }
      const base = baseRef.current;
      if (percent && base.balance > 0) {
        lines.push(label('Return since left edge', fmtPct(((row.adjusted - base.adjusted) / base.balance) * 100)));
      }
      (groupsByTime.get(row.time) || []).forEach(g => g.items.forEach(item => {
        const flow = FLOW_TYPES[g.kind];
        lines.push(flow
          ? `<span style="color:${flow.color}">${flow.label}</span> ${fmt(item.amount)}${accountSuffix(item.account_id)}`
          : `<span style="color:${markerColors[g.kind]}">${g.kind === 'buy' ? 'Buy' : 'Sell'}</span> ${fmtQty(item.quantity)} ${esc(item.symbol)} @ ${fmt(item.price_per_share)}${accountSuffix(item.account_id)}`);
      }));
      legendEl.innerHTML = lines.join('<br>');
    };
    const legendHandler = param => {
      const row = param.point && param.time != null ? rowByTime.get(param.time) : null;
      renderLegend(row);
      if (!row || !legendEl) return;
      // Right of the cursor, flipping to its left over the right half of the chart.
      const left = param.point.x > node.clientWidth / 2
        ? param.point.x - 12 - legendEl.offsetWidth
        : param.point.x + 12;
      legendEl.style.left = Math.max(0, Math.min(left, node.clientWidth - legendEl.offsetWidth - 4)) + 'px';
      // Sits on the far side of the cursor from the line, so it never covers it:
      // above the cursor when the crosshair is above the line, below otherwise.
      const lineY = series.priceToCoordinate(percent ? row.adjusted : row.balance);
      const above = lineY != null && param.point.y < lineY;
      legendEl.style.top = (above ? Math.max(0, param.point.y - 12 - legendEl.offsetHeight) : param.point.y + 12) + 'px';
    };

    const hoverHandler = param => {
      chart.chartElement().style.cursor = groupsById.has(param.hoveredObjectId) ? 'pointer' : '';
    };
    const clickHandler = param => {
      const g = groupsById.get(param.hoveredObjectId);
      const go = onNavigateRef.current;
      if (!g || !go) return;
      const account = accountId ? { account_id: accountId } : {};
      if (FLOW_TYPES[g.kind]) return go('transactions', { tab: 'cash', ...account });
      const symbols = [...new Set(g.items.map(t => t.symbol))];
      go('transactions', { tab: 'trades', ...account, ...(symbols.length === 1 ? { symbol: symbols[0] } : {}) });
    };

    // Rebase to the bars in view whenever the window moves.
    const rangeHandler = logical => {
      if (!logical) return;
      const from = Math.min(Math.max(Math.ceil(logical.from), 0), rows.length - 1);
      const to = Math.max(Math.min(Math.floor(logical.to), rows.length - 1), from);
      baseRef.current = { adjusted: rows[from].adjusted, balance: rows[from].balance };
      setView(v => (v && v.from === from && v.to === to ? v : { from, to }));
    };

    chart.subscribeCrosshairMove(legendHandler);
    chart.subscribeCrosshairMove(hoverHandler);
    chart.subscribeClick(clickHandler);
    chart.timeScale().subscribeVisibleLogicalRangeChange(rangeHandler);

    return () => {
      chart.unsubscribeCrosshairMove(legendHandler);
      chart.unsubscribeCrosshairMove(hoverHandler);
      chart.unsubscribeClick(clickHandler);
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(rangeHandler);
      chart.remove();
      chartRef.current = null;
      if (legendEl) { legendEl.innerHTML = ''; legendEl.style.display = 'none'; }
    };
  }, [rows, groups, mode, markerColors, accounts, accountId, hasAccrued, withAccrued]);

  // Apply the selected range. A range reaching past the first day of data clamps to it.
  const rangeDays = RANGES.find(r => r.id === range).days;
  const rangeStart = rows.length && rangeDays != null ? addDays(rows[rows.length - 1].time, -rangeDays) : null;
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || rows.length < 2) return;
    let from = 0;
    if (rangeStart) {
      const index = rows.findIndex(r => r.time >= rangeStart);
      from = index < 0 ? rows.length - 1 : index;
    }
    from = Math.min(from, rows.length - 2); // always show at least two days
    chart.timeScale().setVisibleLogicalRange({ from: from - 0.5, to: rows.length - 0.5 });
  }, [range, rangeStart, rows, mode, groups, markerColors, accounts, accountId, hasAccrued, withAccrued]);

  if (history === null) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="modal-title">Portfolio Value</div>
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Loading balance history...</div>
      </div>
    );
  }
  if (rows.length < 2) {
    return (
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="modal-title">Portfolio Value</div>
        <div className="muted" style={{ fontSize: 13 }}>
          Balance history will appear here once there are at least two days of activity and prices.
        </div>
      </div>
    );
  }

  const first = rows[view ? Math.min(view.from, rows.length - 1) : 0];
  const last = rows[view ? Math.min(view.to, rows.length - 1) : rows.length - 1];
  const change = last.balance - first.balance;
  const flows = last.flows - first.flows;
  const gain = change - flows;
  const gainPct = first.balance > 0 ? (gain / first.balance) * 100 : null;
  const clamped = rangeStart && rangeStart < rows[0].time;

  const smallButton = { fontSize: 12, padding: '3px 10px' };

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div className="modal-title">Portfolio Value</div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        <div>
          <div className="stat-label">Balance · {last.time}</div>
          <div className="stat-value mono">{fmt(last.balance)}</div>
        </div>
        <div>
          <div className="stat-label">Change since {first.time}</div>
          <div className={`mono ${change >= 0 ? 'gain' : 'loss'}`} style={{ fontSize: 16 }}>{fmtSigned(change)}</div>
        </div>
        <div title="Balance change minus deposits, opening balances and withdrawals in this window. Dividends, interest and fees count as return.">
          <div className="stat-label">Return (net of deposits and withdrawals)</div>
          <div className={`mono ${gain >= 0 ? 'gain' : 'loss'}`} style={{ fontSize: 16 }}>
            {fmtSigned(gain)}{gainPct != null ? ` (${fmtPct(gainPct)})` : ''}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {hasAccrued && (
            <button
              className={includeAccrued ? 'btn' : 'btn-ghost'}
              style={smallButton}
              title="Include estimated money market income that has accrued but not yet been paid"
              onClick={() => setIncludeAccrued(v => !v)}
            >
              Accrued income
            </button>
          )}
          {['$', '%'].map(m => (
            <button key={m} className={mode === m ? 'btn' : 'btn-ghost'} style={smallButton}
              title={m === '$' ? 'Plot the balance' : 'Plot cumulative % return from the left edge of the view'}
              onClick={() => setMode(m)}>{m}</button>
          ))}
          <span style={{ width: 8 }} />
          {RANGES.map(r => (
            <button key={r.id} className={range === r.id ? 'btn' : 'btn-ghost'} style={smallButton} onClick={() => setRange(r.id)}>{r.id}</button>
          ))}
        </div>
      </div>

      <div style={{ position: 'relative', height: 300 }}>
        <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
        <div
          ref={legendRef}
          style={{
            display: 'none', position: 'absolute', zIndex: 3, pointerEvents: 'none',
            background: 'var(--surface)', border: '1px solid var(--border)', padding: '8px 12px', borderRadius: 6,
            fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5, whiteSpace: 'nowrap',
          }}
        />
      </div>

      <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        {clamped && <span>Data from {rows[0].time}. </span>}
        {withAccrued && <span>Balance includes estimated accrued money market income. </span>}
        {history.unpriced_symbols.length > 0 && (
          <span>No price history for {history.unpriced_symbols.join(', ')}: valued at the last trade price. </span>
        )}
        Arrows mark buys and sells; circles mark deposits, opening balances and withdrawals.
      </div>
    </div>
  );
}
